import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';

/**
 * interfaces
 */
import { 
  IAuthGetNewTokenApi, 
  IAuthLoginApi,
  IAuthLogoutApi,
  IAuthForgotPasswordApi,
  IAuthResetPasswordApi,
  IAuthCheckResetTokenApi,
  IForgotPasswordBodyRequest,
  IResetPasswordBodyRequest
} from './interfaces';
import { IAuthLogin } from '../../interfaces';

/**
 * constants
 */
import { AuthenticationConst } from '../consts';

/**
 * utilities
 */
import { IJwtResponse, IJwtWithRefreshResponse } from '@core/utilities';

/**
 * httpHandler
*/
import { AppHttpUtils, IResponse, SessionOrchestratorUtils } from '@network';

/**
 * user
 */
import { UserConst } from '@identity/user';

/**
 * Authentication API Service
 * @class AuthenticationApiService
 * @description Handles HTTP API calls for authentication operations.
 * Provides methods to interact with authentication endpoints and manage JWT tokens.
 * Implements the IAuthLoginApi, IAuthLogoutApi, and IAuthGetNewTokenApi interfaces to provide a complete authentication solution.
 * 
 * @example
 * ```typescript
 * this.authenticationApiService.login({ email: 'test@test.com', password: 'password', totp_code: '123456' }).subscribe((response) => {
 *   console.log('Logged in successfully');
 * });
 * ```
 */
@Injectable()
export class AuthenticationApiService
  implements IAuthLoginApi, IAuthLogoutApi, IAuthGetNewTokenApi, IAuthForgotPasswordApi, IAuthResetPasswordApi, IAuthCheckResetTokenApi {

  /**
   * 
   */
  constructor(private _http: HttpClient) { }

  /**
   * @param body - The body of the login request
   * @returns {Observable<IJwtResponse>} Observable stream of IJwtResponse
   * @description Logs in the user by making a POST request to the login endpoint with the body of the login request
   * 
   * @example
   * ```typescript
   * this.authenticationApiService.login({ email: 'test@test.com', password: 'password', totp_code: '123456' }).subscribe((response) => {
   *   console.log('Logged in successfully');
   * });
   * ```
   */
  public login(body: IAuthLogin): Observable<IJwtWithRefreshResponse> {

    // Create context
    const context = new HttpContext();

    AppHttpUtils.withBypassJwt(context);
    AppHttpUtils.withBypassErrorResponse(context);
    AppHttpUtils.withToastMessages({ message: 'AUTH.LOGIN.LOGIN_SUCCESSFUL' }, context);

    return this._http.post<IJwtWithRefreshResponse>(AuthenticationConst.LOGIN_ENDPOINT, body, { context });

  }

  /**
   * @param refreshToken - The refresh token to get a new access token by
   * @returns {Observable<IJwtResponse>} Observable stream of IJwtResponse
   * @description Gets a new access token by making a POST request to the refresh endpoint with the refresh token in the Authorization header
   * 
   * @example
   * ```typescript
   * this.authenticationApiService.getNewAccessTokenByRefreshToken('refresh_token').subscribe((response) => {
   *   console.log('New access token received');
   * });
   * ```
   */
  public getNewAccessTokenByRefreshToken(refreshToken: string): Observable<IJwtResponse> {

    // Create context
    const context = new HttpContext();

    // Set bypass contexts
    AppHttpUtils.withBypassJwt(context);
    AppHttpUtils.withBypassToast(context);
    SessionOrchestratorUtils.withBypass(context);

    return this._http.post<IJwtResponse>(AuthenticationConst.REFRESH_ENDPOINT, null, {
      context,
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      }
    });

  }

  /**
   * Logs out the user by making a POST request to the logout endpoint with the JWT token in the Authorization header.
   * @param token - The JWT token to logout with
   * @returns {Observable<unknown>} Observable stream of unknown response
   * @description Logs out the user by making a POST request to the logout endpoint with the JWT token in the Authorization header.
   * 
   * @example
   * ```typescript
   * this.authenticationApiService.LOGOUT_AUTH('your-jwt-token').subscribe((response) => {
   *   console.log('Logged out successfully');
   * });
   * ```
   */
  public logout(token: string): Observable<unknown> {

    // Create context
    const context = new HttpContext();
    
    // Set bypass contexts
    AppHttpUtils.withBypassJwt(context);
    AppHttpUtils.withBypassToast(context);
    AppHttpUtils.withBypassErrorResponse(context);
    SessionOrchestratorUtils.withBypass(context);

    return this._http.post<void>(AuthenticationConst.LOGOUT_ENDPOINT, {}, {
      context,
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
  }

  /**
   * Sends forgot password request
   * @param body - IForgotPasswordRequest
   * @returns Observable<IResponse<string>>
   */
  public forgotPassword(body: IForgotPasswordBodyRequest): Observable<IResponse<string>> {
    
    // create request context
    const context = new HttpContext();
    
    // set bypass contexts
    AppHttpUtils.withBypassJwt(context);
    AppHttpUtils.withBypassToast(context);
    AppHttpUtils.withBypassErrorResponse(context);

    return this._http.post<IResponse<string>>(UserConst.FORGOT_PASSWORD_ENDPOINT, body, { context });
    
  }

  /**
  * Sends Reset Password request
  * @param body - IResetPasswordBodyRequest
  * @returns Observable<IResponse<string>>
  */
  public resetPassword(body: IResetPasswordBodyRequest): Observable<IResponse<string>> {

    // create request context
    const context = new HttpContext();

    // set bypass contexts
    AppHttpUtils.withBypassJwt(context);
    AppHttpUtils.withBypassToast(context);
    AppHttpUtils.withBypassErrorResponse(context);

    return this._http.post<IResponse<string>>(UserConst.RESET_PASSWORD_ENDPOINT, body, { context });

  };

  /**
   * Checks if the provided reset token is valid.
   * @param token - The reset token to validate
   * @returns {Observable<IResponse<string>>} Observable emitting API response
   *
   * @example
   * ```typescript
   * this.authCheckResetTokenApi.checkResetToken('Zlsj-GO04sb7PWUjLOMQ...')
   *   .subscribe(response => console.log(response.data));
   * ```
   */
  public checkResetToken(token: string): Observable<IResponse<string>> {
    
    // create request context
    const context = new HttpContext();

    // set bypass contexts
    AppHttpUtils.withBypassJwt(context);
    AppHttpUtils.withBypassToast(context);
    AppHttpUtils.withBypassErrorResponse(context);

    return this._http.get<IResponse<string>>(UserConst.CHECK_RESET_TOKEN_ENDPOINT(token), { context });

  }

}